import React from 'react';
import { Line, Circle, Path } from 'react-konva';
import {
  WALL_RENDER_PROPERTIES,
  DOOR_TYPES,
  DOOR_DIRECTIONS,
  DOOR_RENDER_PROPERTIES
} from '../dataModels';

// --------------------------------------------------
// Helpers
// --------------------------------------------------
function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

// Arc as a wedge (hinge-centered)
function createArcPath(cx, cy, startAngle, endAngle, radius, steps = 24) {
  // Always draw in the shorter direction but keep sign consistent with requested sweep
  // (Konva Path fill works well with a wedge)
  const path = [];
  path.push(`M ${cx} ${cy}`);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const angle = startAngle + (endAngle - startAngle) * t;
    const x = cx + Math.cos(angle) * radius;
    const y = cy + Math.sin(angle) * radius;
    path.push(i === 0 ? `L ${x} ${y}` : `L ${x} ${y}`);
  }
  path.push('Z');
  return path.join(' ');
}

// Arrow head lines for an arrow pointing at (tx,ty) from (sx,sy)
function arrowHeadPoints(tx, ty, angle, size = 0.08, spread = 0.6) {
  // returns two small segments end->(end - dir rotated +/- spread)
  const a1 = angle + Math.PI - spread;
  const a2 = angle + Math.PI + spread;
  return [
    [tx, ty, tx + Math.cos(a1) * size, ty + Math.sin(a1) * size],
    [tx, ty, tx + Math.cos(a2) * size, ty + Math.sin(a2) * size]
  ];
}

// --------------------------------------------------
// DoorRenderer
// --------------------------------------------------
export function DoorRenderer({ door, wall, roomCenter }) {
  if (!door || !wall?.startPoint || !wall?.endPoint) return null;

  // ---------------------------
  // Wall geometry
  // ---------------------------
  const x1 = wall.startPoint.x;
  const y1 = wall.startPoint.y;
  const x2 = wall.endPoint.x;
  const y2 = wall.endPoint.y;

  const dx = x2 - x1;
  const dy = y2 - y1;

  const wallLen = Math.hypot(dx, dy) || 1;
  const ux = dx / wallLen;
  const uy = dy / wallLen;

  const wallAngle = Math.atan2(uy, ux);

  // ---------------------------
  // Door hinge/base position on wall
  // door.offset is meters from wall.startPoint
  // door.position is 0..1 ratio on the wall
  // ---------------------------
  let offsetMeters =
    door.offset !== undefined
      ? door.offset
      : door.position !== undefined
      ? door.position * wallLen
      : wallLen / 2;

  // Keep it on the segment
  offsetMeters = clamp(offsetMeters, 0, 0.1);

  const baseX = x1 + ux * offsetMeters;
  const baseY = y1 + uy * offsetMeters;

  const doorType = door.type || DOOR_TYPES.SWING;
  const doorDir = door.direction || DOOR_DIRECTIONS.LEFT;

  const doorWidth =
    DOOR_RENDER_PROPERTIES?.[doorType]?.width ??
    DOOR_RENDER_PROPERTIES?.default?.width ??
    0.9;

  // thickness might be useful later (for offsets), keep for completeness
  const wallThickness =
    WALL_RENDER_PROPERTIES?.[wall.type]?.thickness ??
    WALL_RENDER_PROPERTIES?.default?.thickness ??
    0.2;

  // Styling (keep in meters; parent scales decide pixels)
  const trackStroke = '#2196F3';
  const arrowStroke = '#1976D2';

  // ==================================================
  // SLIDING DOOR
  // - Arrow(s) PARALLEL to wall
  // - Add double-arrow for BOTH
  // ==================================================
  if (doorType === DOOR_TYPES.SLIDING) {
    // Track segment along the wall direction
    const trackStartX = baseX;
    const trackStartY = baseY;
    const trackEndX = baseX + ux * doorWidth;
    const trackEndY = baseY + uy * doorWidth;

    const centerX = (trackStartX + trackEndX) / 2;
    const centerY = (trackStartY + trackEndY) / 2;

    // Arrow is parallel to wall
    // We'll draw it centered and shorter than the track
    const arrowSpan = Math.min(0.5, doorWidth * 0.6); // meters
    const half = arrowSpan / 2;

    const ax1 = centerX - ux * half;
    const ay1 = centerY - uy * half;
    const ax2 = centerX + ux * half;
    const ay2 = centerY + uy * half;

    // Determine direction:
    // LEFT  => arrow points against wall direction (towards wall.start)
    // RIGHT => arrow points along wall direction (towards wall.end)
    // BOTH  => double-headed arrow
    const isBoth = doorDir === DOOR_DIRECTIONS.BOTH;
    const isRight = doorDir === DOOR_DIRECTIONS.RIGHT;

    // Single arrow end
    const tipX = isRight ? ax2 : ax1;
    const tipY = isRight ? ay2 : ay1;
    const dirAngle = isRight ? wallAngle : wallAngle + Math.PI;

    const headSegs = arrowHeadPoints(tipX, tipY, dirAngle, 0.08, 0.6);

    // For double arrow, we also add head on the other side
    const tipX2 = isRight ? ax1 : ax2;
    const tipY2 = isRight ? ay1 : ay2;
    const dirAngle2 = dirAngle + Math.PI;
    const headSegs2 = arrowHeadPoints(tipX2, tipY2, dirAngle2, 0.08, 0.6);

    return (
      <>
        {/* Track */}
        <Line
          points={[trackStartX+0.05, trackStartY + 0.05, trackEndX+0.05, trackEndY + 0.05]}
          stroke={trackStroke}
          strokeWidth={0.9}
          strokeScaleEnabled={false}
          listening={false}
        />

        {/* Arrow shaft (parallel) */}
        <Line
          points={[ax1+0.05, ay1 + 0.05, ax2+0.05, ay2 + 0.05]}
          stroke={arrowStroke}
          strokeWidth={0.9}
          strokeScaleEnabled={false}
          listening={false}
        />

        {/* Arrow head(s) */}
        {!isBoth &&
          headSegs.map((pts, i) => (
            <Line
              key={`h1-${i}`}
              points={pts}
              stroke={arrowStroke}
              strokeWidth={0.9}
              strokeScaleEnabled={false}
              listening={false}
            />
          ))}

        {isBoth && (
          <>
            {headSegs.map((pts, i) => (
              <Line
                key={`h1-${i}`}
                points={pts}
                stroke={arrowStroke}
                strokeWidth={0.02}
                strokeScaleEnabled={false}
                listening={false}
              />
            ))}
            {headSegs2.map((pts, i) => (
              <Line
                key={`h2-${i}`}
                points={pts}
                stroke={arrowStroke}
                strokeWidth={0.02}
                strokeScaleEnabled={false}
                listening={false}
              />
            ))}
          </>
        )}
      </>
    );
  }

  // ==================================================
  // SWING DOOR
  // - Single side must be EXACT 90° arc
  // - LEFT/RIGHT should not accidentally produce ~30° (bug was choosing
  //   a near angle; here we force 90° from wall direction)
  // - BOTH => show 2 x 90° arcs, one each side
  // ==================================================
  const swingBoth = doorDir === DOOR_DIRECTIONS.BOTH;

  // Two perpendiculars to wall
  const perpLeft = wallAngle + Math.PI / 2;
  const perpRight = wallAngle - Math.PI / 2;

  // Decide inward side if roomCenter exists; otherwise fall back to direction
  // Your request: "fix sliding left/right to generate arc 90°" => this is swing.
  // We will force the open angle to be EXACTLY +/- 90° from the wall.
  let openAngle = perpLeft;

  if (roomCenter && typeof roomCenter.x === 'number' && typeof roomCenter.y === 'number') {
    // Choose the perpendicular that points more toward room center
    const toCenterX = roomCenter.x - baseX;
    const toCenterY = roomCenter.y - baseY;

    const dotL = Math.cos(perpLeft) * toCenterX + Math.sin(perpLeft) * toCenterY;
    const dotR = Math.cos(perpRight) * toCenterX + Math.sin(perpRight) * toCenterY;

    const inward = dotL >= dotR ? perpLeft : perpRight;

    // If user explicitly set LEFT/RIGHT, respect it by mapping to perpendiculars.
    // Otherwise use inward.
    if (doorDir === DOOR_DIRECTIONS.LEFT) openAngle = perpLeft;
    else if (doorDir === DOOR_DIRECTIONS.RIGHT) openAngle = perpRight;
    else openAngle = inward;
  } else {
    // No room center – rely on direction
    if (doorDir === DOOR_DIRECTIONS.RIGHT) openAngle = perpRight;
    else openAngle = perpLeft; // default/LEFT
  }

  // Arc radius and angles:
  // Closed position is along the wall direction (wallAngle).
  // For a true 90° swing, sweep from wallAngle to openAngle.
  const closedAngle = wallAngle;
  const arcRadius = doorWidth; // feel free to change to doorWidth * 0.9

  const arcs = [];

  if (swingBoth) {
    // Two 90° arcs: one to left perp, one to right perp
    arcs.push({ start: closedAngle, end: perpLeft });
    arcs.push({ start: closedAngle, end: perpRight });
  } else {
    arcs.push({ start: closedAngle, end: openAngle });
  }

  // Door leaf endpoint at OPEN position (single only)
  const leafEndX = baseX + Math.cos(openAngle) * doorWidth;
  const leafEndY = baseY + Math.sin(openAngle) * doorWidth;

  return (
    <>
      {/* Swing arc(s) */}
      {arcs.map((a, i) => (
        <Path
          key={`arc-${i}`}
          data={createArcPath(baseX, baseY, a.start, a.end, arcRadius, 28)}
          stroke={trackStroke}
          strokeWidth={0.01}
          strokeScaleEnabled={false}
          fill="rgba(33,150,243,0.12)"
          listening={false}
        />
      ))}

      {/* Door leaf (single swing only) */}
      {!swingBoth && (
        <Line
          points={[baseX, baseY, leafEndX, leafEndY]}
          stroke={trackStroke}
          strokeWidth={0.02}
          strokeScaleEnabled={false}
          listening={false}
        />
      )}

      {/* Hinge */}
      <Circle
        x={baseX}
        y={baseY}
        radius={0.03}
        fill={trackStroke}
        listening={false}
      />
    </>
  );
}